import { Component } from '@angular/core';
import { ApplyColumnStateParams, ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { BusyIndicatorModule } from 'src/app/Busy-Indicator';
import { AgGridOptionsBuilder } from 'src/app/Compliance/AgGrid';
import { IWeissmanModalComponent } from 'src/app/Compliance/WeissmanModalService';
import { ModalWrapperComponent } from 'src/app/UI-Lib/Modal-Wrapper/modalWrapper.component';
import { REFERRAL_ADD_APPEAL_COLUMNS } from './referralAppealColumns';
import { DecimalPipe } from '@angular/common';
import { ReferralsService } from '../../referrals.service';
import { WsAgGridWrapperComponent } from 'src/app/Compliance/AgGrid/AgGridWrapper/wsAgGridWrapper.component';
import { WeissmanDateFormatPipe } from '../../../../UI-Lib/Pipes/Date-Format/date-formatting.pipe';

export interface AddAppealsModalParams {
    referralId: number;
}

export interface AddAppealsModalResult {
    didAdd: boolean;
}

@Component({
    selector: 'add-remove-appeals-modal',
    template: `
        <ws-modal-wrapper
            headerLabel="Add Appeals"
            (save)="addAppeals()"
            (cancel)="cancel()"
            acceptLabel="Add"
            [disableSave]="!selectedAppeals.length"
        >
            <ws-ag-grid-angular
                [gridId]="gridId"
                [gridOptions]="gridOptions"
                rowSelection="multiple"
                (selectionChanged)="onSelectionChanged($event)"
                overlayNoRowsTemplate="No Appeals Available"
                [showConfigureLayout]="false"
                (gridReady)="onAgGridReady($event)"
            >
            </ws-ag-grid-angular>
        </ws-modal-wrapper>
    `,
    imports: [ModalWrapperComponent, BusyIndicatorModule, WsAgGridWrapperComponent],
})
export class AddAppealsModalComponent implements IWeissmanModalComponent<AddAppealsModalParams, AddAppealsModalResult> {
    constructor(
        private readonly _bsModalRef: BsModalRef,
        private readonly _decimalPipe: DecimalPipe,
        private readonly _datePipe: WeissmanDateFormatPipe,
        private readonly _referralsService: ReferralsService,
    ) {}

    params: AddAppealsModalParams;
    result: AddAppealsModalResult = { didAdd: false };

    selectedAppeals: Core.LitigationReferralEligibleAppealModel[] = [];

    gridId: System.Guid = '65b8ae86-d5e6-4279-aa62-d65fc92738ec';
    gridOptions: GridOptions = new AgGridOptionsBuilder()
        .withContext(this)
        .withLoadingOverlay()
        .withMultipleColumnSort()
        .withColumnResize()
        .withLoadingOverlay()
        .withTextSelection()
        .withColumnPinning()
        .build();

    private _gridApi: GridApi;
    private _eligibleAppeals: Core.LitigationReferralEligibleAppealModel[];

    async onAgGridReady(grid: GridReadyEvent): Promise<void> {
        this._gridApi = grid.api;
        this._gridApi.setGridOption('loading', true);

        try {
            this._eligibleAppeals = await this._referralsService.getLitigationReferralEligibleAppeals(
                this.params.referralId,
            );

            const columns: ColDef[] = REFERRAL_ADD_APPEAL_COLUMNS(this._decimalPipe, this._datePipe);

            const defaultSortModel: ApplyColumnStateParams = {
                state: [{ colId: 'siteStateAbbr', sort: 'asc' }],
                defaultState: { sort: null },
            };

            this._gridApi.setGridOption('columnDefs', columns);
            this._gridApi.applyColumnState(defaultSortModel);

            this._gridApi.setGridOption('rowData', this._eligibleAppeals);
        } finally {
            this._gridApi.setGridOption('loading', false);
        }
    }

    onSelectionChanged(event: any): void {
        this.selectedAppeals = this._gridApi.getSelectedRows();
    }

    async addAppeals(): Promise<void> {
        this._gridApi.setGridOption('loading', true);

        try {
            await this._referralsService.addAppealsToLitigationReferral(
                this.params.referralId,
                this.selectedAppeals.map((x) => x.appealId),
            );

            this.result = { didAdd: true };
            this._bsModalRef.hide();
        } finally {
            this._gridApi.setGridOption('loading', false);
        }
    }

    cancel() {
        this._bsModalRef.hide();
    }
}
